import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, getMoviesByCategory, type Movie } from '@/mocks/movies';

const categoryNames: Record<string, string> = {};
categories.forEach((c) => {
  categoryNames[c.slug] = c.name;
});

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug);
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>([]);

  useEffect(() => {
    document.title = `${categoryNames[slug] || '全部'}电影 - 星空影院 | 热门电影排行榜在线观看`;
    setActiveCategory(slug);
    setFilteredMovies(getMoviesByCategory(slug));
    window.scrollTo(0, 0);
  }, [slug]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
  };

  return (
    <>
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1400px] mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 cursor-pointer whitespace-nowrap transition-colors">首页</Link>
              <span>/</span>
              <span className="text-foreground-300">{categoryNames[slug] || '全部'}电影</span>
            </nav>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
                {categoryNames[slug] || '全部'}电影
              </h1>
              <p className="text-sm text-foreground-500">
                共 {filteredMovies.length} 部电影 - 星空影院热门电影排行榜最新上映大全
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100/80 border border-background-200/60 text-foreground-400 hover:text-foreground-100 hover:border-primary-500/40'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {filteredMovies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/movie/${movie.id}`}
                  className="group flex flex-col cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
                    <img
                      src={movie.coverImage}
                      alt={movie.title}
                      title={`${movie.title} - ${categoryNames[slug]}电影在线观看`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/90">
                        <i className="ri-play-fill text-background-50 text-xl"></i>
                      </div>
                    </div>
                    <div className="absolute top-2 right-2">
                      <div className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-background-50/80 text-foreground-950 text-[10px] font-semibold whitespace-nowrap">
                        <i className="ri-star-fill text-primary-500 text-[8px]"></i>
                        {movie.rating}
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-100 group-hover:text-primary-500 transition-colors truncate">
                    {movie.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{movie.year} / {movie.region}</p>
                </Link>
              ))}
            </div>

            {filteredMovies.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 border border-background-200/60 mb-4">
                  <i className="ri-movie-2-line text-foreground-500 text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无电影</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${categoryNames[slug] || '全部'}电影 - 星空影院`,
            description: `星空影院${categoryNames[slug] || '全部'}电影分类，提供热播电影、热门电影排行榜、最新上映电影免费在线观看。`,
            url: `https://xingkongcinema.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '星空影院',
              url: 'https://xingkongcinema.com/',
            },
          }),
        }}
      />
    </>
  );
}